/**
 * This enum should list all valid paths of the app.
 * Typed router components and hooks, such as `TypedLink`,
 * validate nav targets and path params against these paths.
 */
export enum Path {
  Root = '',
  Auth = '/auth',
  GuestSignup = '/guest-signup',
  ActionHandlerAction = '/action-handler/action',
  NotFound = '/404',

  // Workspace pages
  WorkspaceCreate = '/workspace/create',
  // Using `workspaceJoinId` instead of `workspaceId` so components relying on
  // `workspaceId` will not pick it up accidentally.
  WorkspaceJoinViaInvitation = '/join/:workspaceJoinId/:invitationId?',
  Workspace = '/workspace/:workspaceId',

  // Editor
  FlowEditorRoot = '/workspace/:workspaceId/flow/:flowDefinitionId/edit',
  FlowEditorSession = '/workspace/:workspaceId/flow/:flowDefinitionId/edit/session/:sessionId',

  // Templates page
  TemplatesRoot = '/workspace/:workspaceId/templates',

  // Talent Acquisition pages
  AcquisitionRoot = '/workspace/:workspaceId/talent-acquisition',
  AcquisitionOpenings = '/workspace/:workspaceId/talent-acquisition/openings',
  AcquisitionFlowDefinition = '/workspace/:workspaceId/opening/:flowDefinitionId',

  // Talent Development pages
  DevelopmentRoot = '/workspace/:workspaceId/development-hub',
  DevelopmentFlows = '/workspace/:workspaceId/development-hub/flows',
  DevelopmentFlowDefinition = '/workspace/:workspaceId/flow/:flowDefinitionId',
  DevelopmentFlowDefinitionHistory = '/workspace/:workspaceId/flow/:flowDefinitionId/history',

  // Flow instance pages
  FlowInstance = '/workspace/:workspaceId/flow/:flowDefinitionId/instance/:flowInstanceId',
  Session = '/workspace/:workspaceId/flow/:flowDefinitionId/instance/:flowInstanceId/session/:sessionId',

  // Shared flow instance pages
  SharedEmbeddedFlowInstance = '/share/:shareId',
  SharedEmbeddedSession = '/share/:shareId/session/:sessionId',
  AcquisitionInvitationEmbeddedFlowInstance = '/candidate-invite/:shareId',
  CandidateEmbeddedSession = '/candidate-invite/:shareId/session/:sessionId',

  // Flow editor pages
  FlowEditorHome = '/flow-editor',
  FlowVersionEditor = '/flow-editor/:flowProjectId',
  FlowVersionSessionBundles = '/flow-editor/:flowProjectId/session-bundles',
  FlowVersionModules = '/flow-editor/:flowProjectId/modules',
  FlowVersionSettings = '/flow-editor/:flowProjectId/settings',

  // Session editor pages
  SessionEditor = '/flow-editor/:flowProjectId/session-editor/:sessionBundleId/:sessionVersionId',
  SessionEditorPreview = '/flow-editor/:flowProjectId/session-editor/:sessionBundleId/:sessionVersionId/preview',

  // System Admin pages
  SystemAdmin = '/sysadmin',
  SystemAdminFlows = '/sysadmin/flows',
  SystemAdminWorkspaces = '/sysadmin/workspaces',
  SystemAdminBilling = '/sysadmin/billing',
}

export function buildPath(
  path: Path,
  params?: Record<string, string | number>,
  search?: URLSearchParams,
) {
  let url = path.startsWith('/') ? path : `/${path}`;
  if (params && typeof params === 'object') {
    Object.keys(params).forEach((key) => {
      url = url.replaceAll(`:${key}`, (params[key] as string) ?? '');
    });
  }

  if (search) {
    url += `?${search}`;
  }

  return url;
}
